import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Euro, Clock, Edit2, Package, TagIcon } from "lucide-react";
import { api, formatErr } from "../lib/api";

/**
 * PackagesPanel — pacchetti predefiniti (es. "Wellness Day = sauna + massaggio + facciale").
 * Owner-only. Sotto al cofano un pacchetto = combo predefinita con sconto/prezzo fisso.
 */
export default function PackagesPanel({ businessId }) {
  const [services, setServices] = useState([]);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | pkgId
  const [form, setForm] = useState(emptyForm());
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  function emptyForm() {
    return { name: "", description: "", service_ids: [], discount_percent: 0, fixed_price: "", active: true };
  }

  async function load() {
    const [b, p] = await Promise.all([
      api.get(`/businesses/${businessId}`),
      api.get(`/businesses/${businessId}/packages`),
    ]);
    setServices(b.data.services || []);
    setItems(p.data || []);
  }
  useEffect(() => { load(); }, [businessId]);

  function startNew() {
    setEditing("new");
    setForm(emptyForm());
    setErr("");
  }

  function startEdit(pkg) {
    setEditing(pkg.id);
    setForm({
      name: pkg.name,
      description: pkg.description || "",
      service_ids: pkg.service_ids || [],
      discount_percent: pkg.discount_percent || 0,
      fixed_price: pkg.fixed_price ?? "",
      active: pkg.active !== false,
    });
    setErr("");
  }

  function cancel() {
    setEditing(null);
    setForm(emptyForm());
    setErr("");
  }

  function toggleService(sid) {
    setForm((f) => {
      const has = f.service_ids.includes(sid);
      return { ...f, service_ids: has ? f.service_ids.filter((x) => x !== sid) : [...f.service_ids, sid] };
    });
  }

  function moveService(sid, dir) {
    setForm((f) => {
      const arr = [...f.service_ids];
      const idx = arr.indexOf(sid);
      if (idx < 0) return f;
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, service_ids: arr };
    });
  }

  const previewSum = useMemo(() => {
    const list = form.service_ids.map((sid) => services.find((s) => s.id === sid)).filter(Boolean);
    const dur = list.reduce((acc, s) => acc + (s.duration_min || 0) + (s.buffer_min || 0), 0);
    const raw = list.reduce((acc, s) => acc + Number(s.price || 0), 0);
    let final = raw;
    if (form.fixed_price !== "" && !isNaN(Number(form.fixed_price))) {
      final = Number(form.fixed_price);
    } else if (Number(form.discount_percent) > 0) {
      final = Math.round(raw * (1 - Number(form.discount_percent) / 100) * 100) / 100;
    }
    return { dur, raw, final };
  }, [form, services]);

  async function save(e) {
    e.preventDefault();
    setErr("");
    if (form.service_ids.length < 2) {
      setErr("Seleziona almeno 2 servizi");
      return;
    }
    if (form.service_ids.length > 5) {
      setErr("Massimo 5 servizi per pacchetto");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || "",
        service_ids: form.service_ids,
        discount_percent: Number(form.discount_percent) || 0,
        fixed_price: form.fixed_price === "" ? null : Number(form.fixed_price),
        active: form.active,
      };
      if (editing === "new") {
        await api.post(`/businesses/${businessId}/packages`, payload);
      } else {
        await api.put(`/packages/${editing}`, payload);
      }
      cancel();
      await load();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  }

  async function del(pkg) {
    if (!window.confirm(`Eliminare il pacchetto "${pkg.name}"?`)) return;
    await api.delete(`/packages/${pkg.id}`);
    await load();
  }

  const insufficientServices = services.length < 2;

  return (
    <div data-testid="packages-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-xl font-medium">Pacchetti predefiniti</h3>
          <p className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">Combina più servizi in un'unica offerta (es. "Wellness Day = sauna + massaggio + facciale"). Il cliente lo prenota in 1 tap come fosse un servizio singolo.</p>
        </div>
        {!editing && (
          <button
            onClick={startNew}
            disabled={insufficientServices}
            className="btn-accent inline-flex items-center disabled:opacity-50"
            data-testid="package-new-btn"
            title={insufficientServices ? "Serve almeno 2 servizi" : ""}
          >
            <Plus className="h-4 w-4 mr-2" />Nuovo pacchetto
          </button>
        )}
      </div>

      {insufficientServices && !editing && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 mb-4 dark:bg-amber-950/40 dark:border-amber-800/50 dark:text-amber-200">
          Per creare un pacchetto servono almeno 2 servizi configurati.
        </div>
      )}

      {editing && (
        <form onSubmit={save} className="card-soft p-5 mb-6 space-y-4" data-testid="package-form">
          <div>
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wider dark:text-zinc-300">Nome</label>
            <input
              required
              minLength={2}
              maxLength={120}
              className="input-soft mt-1"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Es. Wellness Day"
              data-testid="package-name"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wider dark:text-zinc-300">Descrizione</label>
            <textarea
              rows={2}
              className="input-soft h-auto py-3 mt-1"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Descrizione mostrata al cliente"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wider dark:text-zinc-300">Servizi inclusi (in ordine)</label>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              {services.map((s) => {
                const idx = form.service_ids.indexOf(s.id);
                const sel = idx >= 0;
                return (
                  <div
                    key={s.id}
                    className={`rounded-xl border p-3 cursor-pointer transition ${sel ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 hover:border-zinc-400"}`}
                    onClick={() => toggleService(s.id)}
                    data-testid={`package-svc-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm flex-1 truncate">
                        {sel && <span className="inline-block w-5 text-xs">{idx + 1}.</span>}
                        {s.name}
                      </div>
                      <div className="text-xs opacity-70">{s.duration_min}min · €{s.price}</div>
                    </div>
                    {sel && (
                      <div className="mt-2 flex gap-2 text-xs">
                        <button type="button" className="px-2 py-0.5 rounded bg-white/20 hover:bg-white/30" onClick={(e) => { e.stopPropagation(); moveService(s.id, -1); }}>↑</button>
                        <button type="button" className="px-2 py-0.5 rounded bg-white/20 hover:bg-white/30" onClick={(e) => { e.stopPropagation(); moveService(s.id, +1); }}>↓</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-xs text-zinc-500 mt-2 dark:text-zinc-400">Ordine = sequenza dei servizi nella combo. Min 2, max 5.</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-700 uppercase tracking-wider dark:text-zinc-300">Sconto (%)</label>
              <input
                type="number"
                min={0}
                max={80}
                step="1"
                className="input-soft mt-1"
                value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value, fixed_price: "" })}
                placeholder="0"
                data-testid="package-discount"
              />
              <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">Es. 15 = sconto 15% sul totale.</div>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-700 uppercase tracking-wider dark:text-zinc-300">Prezzo fisso (€)</label>
              <input
                type="number"
                min={0}
                step="0.01"
                className="input-soft mt-1"
                value={form.fixed_price}
                onChange={(e) => setForm({ ...form, fixed_price: e.target.value, discount_percent: 0 })}
                placeholder="(facoltativo)"
                data-testid="package-fixed-price"
              />
              <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">Sovrascrive sconto e somma servizi.</div>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="package-active" />
            <span>Attivo · visibile al cliente sulla pagina di prenotazione</span>
          </label>

          {form.service_ids.length >= 2 && (
            <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 text-sm dark:bg-blue-950/40 dark:border-blue-800/50" data-testid="package-preview">
              <div className="font-medium text-blue-900 dark:text-blue-200">Anteprima</div>
              <div className="text-xs text-blue-800 mt-0.5 dark:text-blue-300">
                Durata totale: <strong>{previewSum.dur} min</strong> · Somma servizi: <strong>€{previewSum.raw.toFixed(2)}</strong>
                {previewSum.final !== previewSum.raw && (
                  <> · Prezzo finale: <strong className="text-emerald-700 dark:text-emerald-300">€{previewSum.final.toFixed(2)}</strong></>
                )}
              </div>
            </div>
          )}

          {err && <div className="text-sm text-red-600">{err}</div>}

          <div className="flex items-center gap-3">
            <button type="submit" disabled={saving} className="btn-accent" data-testid="package-save">
              {saving ? "Salvataggio…" : (editing === "new" ? "Crea pacchetto" : "Aggiorna")}
            </button>
            <button type="button" onClick={cancel} className="text-sm text-zinc-500 dark:text-zinc-400">Annulla</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="card-soft p-5 flex items-start gap-3" data-testid={`package-row-${p.id}`}>
            <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 dark:bg-blue-950/40">
              <Package className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-medium">{p.name}</div>
                {!p.active && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">Disattivato</span>}
              </div>
              {p.description && <div className="text-sm text-zinc-600 mt-0.5 dark:text-zinc-400">{p.description}</div>}
              <div className="text-xs text-zinc-500 flex gap-3 mt-2 flex-wrap dark:text-zinc-400">
                <span className="inline-flex items-center gap-1"><TagIcon className="h-3 w-3" />{(p.services || []).map((s) => s.name).join(" → ")}</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{p.total_duration_min}min</span>
                <span className="inline-flex items-center gap-1"><Euro className="h-3 w-3" />
                  {p.final_price !== p.raw_price
                    ? <><span className="line-through opacity-60">{p.raw_price.toFixed(2)}</span> <strong className="text-emerald-700 dark:text-emerald-300">{p.final_price.toFixed(2)}</strong></>
                    : <strong>{p.final_price.toFixed(2)}</strong>}
                </span>
              </div>
            </div>
            <button onClick={() => startEdit(p)} className="h-9 px-3 rounded-full hover:bg-zinc-100 text-xs text-zinc-600 dark:hover:bg-zinc-800 dark:text-zinc-400" data-testid={`package-edit-${p.id}`}>
              <Edit2 className="h-3.5 w-3.5 inline mr-1" />Modifica
            </button>
            <button onClick={() => del(p)} className="h-9 w-9 rounded-full hover:bg-red-50 hover:text-red-600 flex items-center justify-center text-zinc-400 dark:text-zinc-500 dark:bg-red-950/40" data-testid={`package-del-${p.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && !editing && (
          <div className="text-sm text-zinc-500 text-center py-8 dark:text-zinc-400">Nessun pacchetto creato.</div>
        )}
      </div>
    </div>
  );
}
